import React, { useCallback, useEffect, useState } from "react";
import { Formik } from "formik";
import Button from "react-bootstrap/Button";
import Form from 'react-bootstrap/Form';
import Spinner from "react-bootstrap/Spinner";
import moment from "moment";
import "./styles.module.css";
import UserService from "../services/user.service";
import type { AppointmentItem } from "../pages/models";

type DayOffProps = {
  appointment: AppointmentItem;
  onHide: () => void;
  onAppointmentChange: () => void;
};

type DayOffFormValues = {
  note: string;
  offMeetings: string[];
};

const getApiErrorMessage = (error: unknown): string => {
  if (typeof error === "object" && error !== null) {
    const message = (error as { response?: unknown; message?: string }).message;
    if (message) {
      return message;
    }
  }

  return "Unexpected error";
};

const DayOff = ({ appointment, onHide, onAppointmentChange }: DayOffProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSettingDayOff, setIsSettingDayOff] = useState(false);
  const [note, setNote] = useState("");
  const [sameDayAppointments, setSameDayAppointments] = useState<AppointmentItem[] | null>(null);

  const getSameDate = useCallback(async () => {
    const appointmentDate = moment(appointment.startDateTime);
    try {
      const sameDayAppointmentsResponse = await UserService.getAppointments(
        appointmentDate.startOf('day').toISOString().replace("Z", ""), 
        appointmentDate.endOf('day').toISOString().replace("Z", "")
      );
      setSameDayAppointments(
        (sameDayAppointmentsResponse.data.data || []).filter(
          (appointmentItem: AppointmentItem) => appointmentItem.status === "draft",
        ),
      );
    } catch (error) {
      alert(getApiErrorMessage(error));
    }
  }, [appointment.startDateTime]);

  useEffect(() => {
    if (isSettingDayOff) {
      getSameDate();
    }
  }, [isSettingDayOff, getSameDate]);
 
  const setDayOff = async (values: DayOffFormValues) => {
    setIsSubmitting(true);
    try {
      await UserService.setDayOff(appointment.id, {"note": note, "daysOff": values.offMeetings});
      setTimeout(() => {
        alert("Day Off set");
        onAppointmentChange();
        onHide();
      }, 400);
    } catch (error) {
      alert(getApiErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const openCloseForm = () => {
    setIsSettingDayOff(!isSettingDayOff);
  }

  const DayOffButton = () => (
    <div style={stylingObject.buttonsDiv}>
      <Button
        style={stylingObject.buttonRight}
        variant="warning"
        type="button"
        onClick={openCloseForm}
        disabled={isSubmitting}
      >
        {isSubmitting ? (
          <Spinner
            as="span"
            animation="border"
            size="sm"
            role="status"
            aria-hidden="true"
          />
        ) : (
          <span>Day Off</span>
        )}
      </Button>
    </div>
  );

  const getHoursLabel = (appointmentItem: AppointmentItem) => {
    return moment(appointmentItem.startDateTime).format("h a");
  }

  const DayOffHours = ({
    handleChange,
    values,
  }: {
    handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    values: DayOffFormValues;
  }) => {
    return (
      <Form.Group controlId="offMeetings" style={stylingObject.dayOffCheckbuttonsRow}>
        {sameDayAppointments && sameDayAppointments.map((appointmentItem) => 
          <Form.Check 
            key={appointmentItem.id}
            id={appointmentItem.id}
            type='checkbox'
            label={getHoursLabel(appointmentItem)}
            name='offMeetings'
            defaultChecked={Boolean(
              appointmentItem.id && values.offMeetings.includes(appointmentItem.id),
            )}
            value={appointmentItem.id}
            onChange={handleChange}
            style={stylingObject.dayOffCheckbutton}
          />
        )}
      </Form.Group>
    )
  }

  const DayOffForm = () => (
    <Formik
      onSubmit={setDayOff}
      initialValues={{
        note:"",
        offMeetings: sameDayAppointments
          ? sameDayAppointments
              .map((appointmentItem) => appointmentItem.id)
              .filter((id): id is string => Boolean(id))
          : [],
      }}
    >
      {({
        handleSubmit,
        handleChange,
        values,
        errors,
        isValid,
        isSubmitting
      }) => (
        <Form
          style={stylingObject.form}
          onSubmit={handleSubmit}
        >
          <h6 style={stylingObject.labels}>Add Day Off note</h6>
          <Form.Control
            type="text"
            name="note"
            onChange={e => setNote(e.target.value)}
            value={note}
            autoFocus
            style={stylingObject.input}
          />
          <DayOffHours handleChange={handleChange} values={values} />
          <Button
            type="submit"
            style={stylingObject.buttonCenter}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
              />
            ) : (
              <span>Confirm Day Off</span>
            )}
          </Button>
        </Form>
          )}
    </Formik>
  );

  return (
    <>
      <DayOffButton />
      {isSettingDayOff && (<DayOffForm/>)}
    </>
  );
};

const stylingObject = {
  form: {
    display: "flex",
    flexDirection: "column",
    padding: 10,
    backgroundColor: "#d5d8db",
    borderRadius: 10,
    marginTop: ".4rem",
    marginBottom: "1rem",
  },
  buttonsDiv: {
    display: "flex",
    flexDirection: "row",
    paddingTop: 10,
  },
  input: {
    padding: 5,
    marginBottom: ".4rem",
  },
  labels: {
    textAlign: "center",
  },
  buttonCenter: {},
  buttonRight: {
    marginLeft: "auto",
  },
  dayOffCheckbuttonsRow: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    marginBottom: 10,
  },
  dayOffCheckbutton: {
    paddingTop: 10,
    paddingBottom: 10,
    paddingRight: 30,
    paddingLeft: 30,
  },
} satisfies Record<
  | "form"
  | "buttonsDiv"
  | "input"
  | "labels"
  | "buttonCenter"
  | "buttonRight"
  | "dayOffCheckbuttonsRow"
  | "dayOffCheckbutton",
  React.CSSProperties
>;

export default DayOff;
